// accounts.js
import Swiper from 'swiper';
import { Navigation, Thumbs, Zoom, Keyboard } from 'swiper/modules';

export default (Alpine) => {
  Alpine.data("AWProductGallery", (
    zoomEnabled
  ) => ({
    swiper: null,
    thumbsSwiper: null,
    loading: false,
    zoomEnabled: zoomEnabled,
    zoomActive: false,
    thumbnailDirection: null,
    variant: null,
    desktopQuery: null,
    handlers: null,
    zoomListeners: null,
    slidesPerView: null,

    _loading(loading) {
      this.loading = loading;
      this.$el.classList.toggle('loading', loading);
    },

    getThumbnailDirection() {
      const swiper = this.$el.querySelector('.swiper');

      if (!swiper || !this.desktopQuery.matches) {
        return 'horizontal';
      }

      return swiper.dataset.thumbnailDirection || 'horizontal';
    },

    getSlidesPerView() {
      const swiper = this.$el.querySelector('.swiper');
      const raw = swiper?.dataset.slidesPerView;

      // 'auto' hands the thumbnail sizing back to CSS, so the rail looks the
      // same before and after Swiper initialises.
      if (raw === 'auto') return 'auto';

      const override = Number(raw);

      if (override > 0) return override;

      if (!this.desktopQuery.matches) return 5;

      return this.getThumbnailDirection() === 'vertical' ? 8 : 5;
    },

    init() {
      this.desktopQuery = window.matchMedia('(min-width: 1110px)');
      this.handlers = {
        variantChanged: (event) => this.onVariantChanged(event),
        breakpointChange: () => this.onBreakpointChange(),
      };

      window.addEventListener('variant-changed', this.handlers.variantChanged);
      this.desktopQuery.addEventListener('change', this.handlers.breakpointChange);

      this.setupSwiper();
    },

    destroy() {
      window.removeEventListener('variant-changed', this.handlers.variantChanged);
      this.desktopQuery?.removeEventListener('change', this.handlers.breakpointChange);
      this.destroySwiper();
    },

    /**
     * Only the thumbnail direction depends on the viewport — the markup Liquid
     * renders is the same at every width — so crossing the breakpoint rebuilds
     * the swipers in place instead of refetching the section.
     */
    onBreakpointChange() {
      const directionChanged = this.getThumbnailDirection() !== this.thumbnailDirection;
      const slidesPerViewChanged = this.getSlidesPerView() !== this.slidesPerView;

      if (!directionChanged && !slidesPerViewChanged) return;

      this.destroySwiper();
      this.setupSwiper();
    },

    getLastVisibleThumbnailIndex() {
      const slides = this.thumbsSwiper?.slides;

      if (!slides?.length) return -1;

      for (let i = slides.length - 1; i >= 0; i--) {
        if (slides[i].classList.contains('swiper-slide-visible')) return i;
      }

      const perView = this.getVisibleThumbnailCount();

      return Math.min(this.thumbsSwiper.activeIndex + perView - 1, slides.length - 1);
    },

    getVisibleThumbnailCount() {
      const perView = Math.floor(this.thumbsSwiper?.slidesPerViewDynamic?.() ?? 0);

      return perView > 0 ? perView : Math.floor(Number(this.slidesPerView)) || 1;
    },

    setLastThumbnailOverlay() {
      if (!this.thumbsSwiper) return;

      this.$el.querySelectorAll('.last-thumbnail-overlay').forEach(el => el.remove());

      const slides = this.thumbsSwiper.slides;
      const lastVisibleIndex = this.getLastVisibleThumbnailIndex();

      if (lastVisibleIndex < 0) return;

      const remainingSlides = slides.length - lastVisibleIndex - 1;

      if (remainingSlides <= 0) return;

      const overlay = document.createElement('div');
      overlay.className = 'last-thumbnail-overlay';
      overlay.textContent = `+${remainingSlides}`;
      slides[lastVisibleIndex].appendChild(overlay);
    },

    onVariantChanged(event) {
      const variant = event.detail.variant;
      this.variant = variant;

      this.fetchProductGallerySection(variant);
    },


    async fetchProductGallerySection(variant) {
      const current = this.$el.querySelector('[data-product-gallery]');
      const source = current?.dataset.galleryUrl;

      if (!source || !variant?.id) return;

      this._loading(true)

      const url = `${source}?variant=${variant.id}&view=gallery`;

      let html;

      try {
        const res = await fetch(url);
        html = await res.text();
      } catch (error) {
        // Leaving the loading flag set would hide the gallery for good.
        this._loading(false)
        return;
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-product-gallery]');

      if (!next) {
        this._loading(false)
        return;
      }

      this.destroySwiper();

      current.replaceWith(next);

      this.$nextTick(() => {
        this.setupSwiper();
      });
    },

    setupSwiper() {
      const swiper = this.$el.querySelector('.swiper');
      const thumbsSwiper = this.$el.querySelector('.swiper-thumbs');

      if (!swiper) return;

      this.thumbnailDirection = this.getThumbnailDirection();
      this.slidesPerView = this.getSlidesPerView();

      const zoomConfig = this.zoomEnabled ? {
        maxRatio: 2,
        minRatio: 1,
        panOnlyInZoom: true,
      } : undefined;

      if (thumbsSwiper) {
        this.thumbsSwiper = new Swiper(thumbsSwiper, {
          modules: [Navigation, Keyboard],
          // The 10px gutter is a CSS margin on the slides. With a slide count
          // Swiper subtracts spaceBetween from the width it hands each slide, so
          // it has to match; with 'auto' it measures the margin as part of the
          // slide and would count the gutter twice.
          spaceBetween: this.slidesPerView === 'auto' ? 0 : 10,
          slidesPerView: this.slidesPerView,
          direction: this.thumbnailDirection,
          watchSlidesProgress: true,
          keyboard: {
            enabled: true,
            onlyInViewport: true, // Only trigger when the swiper is visible on screen
          },
          on: {
            slideChange: () => {
              this.setLastThumbnailOverlay();
            },
            resize: () => {
              this.setLastThumbnailOverlay();
            },
            observerUpdate: () => {
              this.setLastThumbnailOverlay();
            },
            click: (swiper) => {
              const lastVisibleIndex = this.getLastVisibleThumbnailIndex();
              const perView = this.getVisibleThumbnailCount();

              if (swiper.clickedIndex !== lastVisibleIndex) return;

              requestAnimationFrame(() => {
                swiper.slideTo(swiper.activeIndex + perView);
              });
            }
          },
          navigation: {
            nextEl: this.$el.querySelector('.swiper-button-next'),
            prevEl: this.$el.querySelector('.swiper-button-prev'),
          },
        });
      }

      this.swiper = new Swiper(swiper, {
        modules: [Navigation, Thumbs, Zoom, Keyboard],
        autoHeight: false,
        zoom: zoomConfig,
        keyboard: {
          enabled: true,
          onlyInViewport: true, // Only trigger when the swiper is visible on screen
        },
        on: {
          slideChange: () => {
            this.setLastThumbnailOverlay();

            this.swiper.el.querySelectorAll('iframe[src*="youtube.com"]').forEach((iframe) => {
              iframe.src = iframe.src;
            });
          },
        },
        thumbs: this.thumbsSwiper
          ? {
              swiper: this.thumbsSwiper,
            }
          : undefined,
      });

      if (this.zoomEnabled) {
        this.setupZoomEventListeners()
      }
      this.setLastThumbnailOverlay();
      this._loading(false)
    },

    destroySwiper() {
      this.zoomListeners?.abort();
      this.zoomListeners = null;

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      if (this.thumbsSwiper) {
        this.thumbsSwiper.destroy(true, true);
        this.thumbsSwiper = null;
      }
    },

    setupZoomEventListeners() {
        this.zoomListeners = new AbortController();
        const { signal } = this.zoomListeners;

        this.swiper.el.addEventListener(
          'pointerdown',
          (ev) => {
          if (ev.target.closest('.zoom-icon')) {
              ev.preventDefault();
              ev.stopPropagation();
          }
          },
          { capture: true, signal }
      );
      
      this.swiper.el.addEventListener('click', (ev) => {
          const btn = ev.target.closest('.zoom-icon');
          if (!btn) return;
          ev.preventDefault();
          ev.stopPropagation();
      
          const scale = this.swiper.zoom?.scale ?? 1;
          this.zoomActive = !this.zoomActive;
      
          if (btn.classList.contains('zoom-out')) {
          this.swiper.zoom.out();
          return;
          }
      
          // zoom-in icon => always center
          if (scale > 1) this.swiper.zoom.out();
          else this.zoomToCenter(this.swiper, 2);
      }, { capture: true, signal });
          
      // Merch zoom handling - Excl zoom icons
      this.swiper.on('click', (swiper, e) => {
          const target = e.target;
          if (target?.closest?.('.swiper-pagination')) return;
          if (target?.closest?.('.zoom-icon')) return;
        
          const scale = swiper.zoom?.scale ?? 1;
          this.zoomActive = !this.zoomActive;

          if (scale > 1) swiper.zoom.out();
          else swiper.zoom.in(e, 2);
      });
    },

    zoomToCenter(swiper, ratio = 2) {
      const zoomEl =
        swiper.slides[swiper.activeIndex]?.querySelector('.swiper-zoom-container') ||
        swiper.el.querySelector('.swiper-slide-active .swiper-zoom-container');
      if (!zoomEl) return;
    
      const zoomTarget = zoomEl.querySelector('.swiper-zoom-target') || zoomEl;
    
      requestAnimationFrame(() => {
        const r = zoomEl.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
    
        const evt = {
          clientX: cx,
          clientY: cy,
          pageX: cx + window.scrollX,
          pageY: cy + window.scrollY,
          target: zoomTarget,
          originalEvent: {
            clientX: cx,
            clientY: cy,
            pageX: cx + window.scrollX,
            pageY: cy + window.scrollY,
            target: zoomTarget,
          },
        };
    
        swiper.zoom.in(evt, ratio);
      });
    },

    
  }));
};